import { useMemo, useState } from 'react';
import { latestEntries } from '@/mocks/home';
import { useToast } from '@/components/base/Toast';
import SectionHeading from '@/pages/home/components/SectionHeading';

type LatestEntrySectionProps = {
  query: string;
};

const filters = [
  { key: 'all', label: '全部入口' },
  { key: 'available', label: '可用' },
  { key: 'maintenance', label: '维护中' },
];

export default function LatestEntrySection({ query }: LatestEntrySectionProps) {
  const [activeFilter, setActiveFilter] = useState('all');
  const showToast = useToast();

  const filtered = useMemo(() => {
    const key = query.trim().toLowerCase();
    return latestEntries.filter((item) => {
      const matchFilter = activeFilter === 'all' || item.status === activeFilter;
      const matchQuery =
        !key || item.name.toLowerCase().includes(key) || item.desc.toLowerCase().includes(key);
      return matchFilter && matchQuery;
    });
  }, [query, activeFilter]);

  return (
    <section id="entries" className="scroll-mt-24 border-b border-background-200 bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            index="02"
            eyebrow="最新入口"
            title="撸撸射最新地址与备用入口列表"
            desc="下表按更新时间与热度排序，标出每条线路的当前状态。找不到入口时，从上往下逐条尝试即可。"
          />

          <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
            {filters.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveFilter(item.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  activeFilter === item.key
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {query.trim() ? (
          <p className="mt-6 text-sm text-foreground-500">
            关键词「<span className="font-semibold text-accent-700">{query.trim()}</span>」找到 {filtered.length} 条入口
          </p>
        ) : null}

        <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
          <div className="hidden grid-cols-12 items-center gap-4 border-b border-background-200 bg-background-100 px-6 py-3 text-xs font-semibold tracking-wide text-foreground-500 md:grid">
            <span className="col-span-5">入口名称</span>
            <span className="col-span-2">状态</span>
            <span className="col-span-2">更新时间</span>
            <span className="col-span-3">热度</span>
          </div>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 bg-background-50 px-6 py-16 text-center">
              <i className="ri-search-eye-line text-3xl text-foreground-300" aria-hidden="true"></i>
              <p className="text-sm text-foreground-500">没有找到匹配的入口，换个关键词试试</p>
            </div>
          ) : (
            filtered.map((item, i) => (
              <div
                key={item.id}
                data-reveal
                style={{ transitionDelay: `${i * 50}ms` }}
                className="grid grid-cols-1 items-center gap-3 border-b border-background-200 bg-background-50 px-5 py-5 transition-colors last:border-b-0 hover:bg-background-100 md:grid-cols-12 md:gap-4 md:px-6"
              >
                <div className="col-span-1 flex items-start gap-3 md:col-span-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-link-m text-xl" aria-hidden="true"></i>
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-base font-bold text-foreground-950">{item.name}</h3>
                      <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-semibold text-primary-800">
                        {item.tag}
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-5 text-foreground-500">{item.desc}</p>
                  </div>
                </div>

                <div className="col-span-1 md:col-span-2">
                  {item.status === 'available' ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700">
                      <span className="inline-block h-2 w-2 rounded-full bg-accent-500"></span>
                      可正常访问
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-500">
                      <span className="inline-block h-2 w-2 rounded-full bg-foreground-400"></span>
                      维护中
                    </span>
                  )}
                </div>

                <div className="col-span-1 text-xs text-foreground-500 md:col-span-2">{item.updated}</div>

                <div className="col-span-1 flex items-center justify-between gap-4 md:col-span-3">
                  <div className="flex flex-1 items-center gap-3">
                    <div className="h-1.5 w-full max-w-[140px] overflow-hidden rounded-full bg-background-200">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${item.heat}%` }}></div>
                    </div>
                    <span className="w-8 shrink-0 text-xs font-bold text-foreground-800">{item.heat}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      showToast(
                        item.status === 'available'
                          ? `正在打开「${item.name}」`
                          : `「${item.name}」维护中，请先使用其他线路`,
                      )
                    }
                    className={`flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3.5 py-2 text-xs font-semibold transition-colors ${
                      item.status === 'available'
                        ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                        : 'border border-background-300 text-foreground-500 hover:border-foreground-400'
                    }`}
                  >
                    访问
                    <i className="ri-arrow-right-up-line text-sm" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}